import {Anchor} from './Anchor';

export enum EventType {
    Target = 'target',
    DataField0 = 'data_field_0',
    DataField1 = 'data_field_1',
    DataField2 = 'data_field_2',
    DataField3 = 'data_field_3',
    Instruction = 'instruction',
    MainVisualisation = 'main_visualisation',
}

export type DataFieldEventType =
    | EventType.DataField0
    | EventType.DataField1
    | EventType.DataField2
    | EventType.DataField3;

interface BaseEvent {
    __uuid: string;
    type: EventType;
    key: any;
    value?: any;
    anchor: Anchor;
}

export enum TargetType {
    StrokeRate = 'stroke_rate',
}

export interface TargetEvent extends BaseEvent {
    type: EventType.Target;
    key: TargetType;
    value: number;
}

export enum DataFieldType {
    HeartRate = 'heart_rate',
    Calories = 'calories',
    PowerWatts = 'power_watts',
    SpeedMps = 'speed_mps',
    Split500M = 'split_500m',
    TargetStrokeRate = 'target_stroke_rate',
    StrokeRate = 'stroke_rate',
    Distanceleft = 'distanceleft',
    TotalDistanceMeters = 'total_distance_meters',
    ElapsedTimeMillis = 'elapsed_time_millis',
    StrokeCount = 'stroke_count',
    Timeleft = 'timeleft',
}

export interface DataField0Event extends BaseEvent {
    type: EventType.DataField0;
    key: DataFieldType;
}

export interface DataField1Event extends BaseEvent {
    type: EventType.DataField1;
    key: DataFieldType;
}

export interface DataField2Event extends BaseEvent {
    type: EventType.DataField2;
    key: DataFieldType;
}

export interface DataField3Event extends BaseEvent {
    type: EventType.DataField3;
    key: DataFieldType;
}

export interface InstructionEvent extends BaseEvent {
    type: EventType.Instruction;
    key: 'text';
    value: string;
}

export enum MainVisualisationType {
    Dial = 'dial',
    GraphHeartRate = 'graph_heart_rate',
    GraphSpeed = 'graph_speed',
    GraphStrokeRate = 'graph_stroke_rate',
    HeartRateZones = 'heart_rate_zones',
    Metronome = 'metronome',
}

export interface MainVisualisationEvent extends BaseEvent {
    type: EventType.MainVisualisation;
    key: MainVisualisationType;
}

export function generateDataFieldText(dataField: DataFieldType): string {
    switch (dataField) {
        case DataFieldType.HeartRate:
            return 'heart rate';
        case DataFieldType.Calories:
            return 'calories';
        case DataFieldType.PowerWatts:
            return 'power (watt)';
        case DataFieldType.SpeedMps:
            return 'speed (mps)';
        case DataFieldType.Split500M:
            return 'split';
        case DataFieldType.TargetStrokeRate:
            return 'stroke rate target';
        case DataFieldType.StrokeRate:
            return 'stroke rate';
        case DataFieldType.Distanceleft:
            return 'distance left';
        case DataFieldType.TotalDistanceMeters:
            return 'total distance meters';
        case DataFieldType.ElapsedTimeMillis:
            return 'elapse time';
        case DataFieldType.StrokeCount:
            return 'stroke count';
        case DataFieldType.Timeleft:
            return 'time left';
    }
}

export function generateTargetTypeText(targetType: TargetType, targetValue: number | null): string {
    switch (targetType) {
        case TargetType.StrokeRate:
            return targetValue === null ? 'stroke rate' : 'stroke rate ' + targetValue;
    }
}

export function generateMainVisualisationText(mainVisualisationType: MainVisualisationType): string {
    switch (mainVisualisationType) {
        case MainVisualisationType.Dial:
            return 'dial (default)';
        case MainVisualisationType.GraphHeartRate:
            return 'heart rate graph';
        case MainVisualisationType.GraphSpeed:
            return 'speed graph';
        case MainVisualisationType.GraphStrokeRate:
            return 'stroke rate graph';
        case MainVisualisationType.HeartRateZones:
            return 'heart rate zone dial';
        case MainVisualisationType.Metronome:
            return 'metronome';
    }
}

export function generateEventTitleName(type: EventType): string {
    switch (type) {
        case EventType.Target:
            return 'target';
        case EventType.DataField0:
            return 'data field top left';
        case EventType.DataField1:
            return 'data field top right';
        case EventType.DataField2:
            return 'data field bottom left';
        case EventType.DataField3:
            return 'data field bottom right';
        case EventType.Instruction:
            return 'instruction';
        case EventType.MainVisualisation:
            return 'visualisation';
    }
}

export type PartEvent =
    | TargetEvent
    | DataField0Event
    | DataField1Event
    | DataField2Event
    | DataField3Event
    | InstructionEvent
    | MainVisualisationEvent;
